import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../../providers";
import StatusButtons from "./StatusButtons";
import StatusIcons from "./StatusIcons";
import { OPERATORS } from "../../../../constants";
import { CustomButton } from "../../../buttons";
import { icon } from "../../../icon-component";
import styles from "./PositionFilter.module.css";
import { useApi, useEffectOnce } from "../../../../hooks";
import { debounce, isNonEmptyArray } from "../../../../utils";
import { TransverseService } from "../../../../../api";

const STANDARD_POSITIONS = {
	myReview: "myReview",
	othersReview: "othersReview",
	othersReviewByUser: "othersReviewByUser",
	myValidation: "myValidation",
};

const debouncedFunction = debounce((func) => func(), 0);

export default function PositionFilter({ expandableOthersReview, value = {}, onChange, partners, readOnly = false }) {
	const { call: getProjectReviewers } = useApi(TransverseService.getProjectReviewers);
	const { call: getUserNameByIds } = useApi(TransverseService.getUserNameByIds);
	const myCompany = useSelector(({ context }) => context.company);
	const projectId = useSelector(({ context }) => context.project?.id);
	const [andOr, setAndOr] = useState(OPERATORS.OR);
	const [fullPartnerSelection, setFullPartnerSelection] = useState([]);
	const [localValue, setLocalValue] = useState([]);
	const [reset, setReset] = useState(false);
	const [reviewersRequest, setReviewersRequest] = useState(undefined);
	const preventReset = useRef(false);
	useEffect(() => {
		let tempPartnerList = [
			{
				id: STANDARD_POSITIONS.myReview,
				name: translate("common:filter.status.my-review"),
				display: true,
				isReviewer: true,
			},
			expandableOthersReview && projectId
				? {
						id: STANDARD_POSITIONS.othersReviewByUser,
						name: translate("common:filter.status.others-review"),
						display: true,
						hasReviewers: true,
						isReviewer: true,
				  }
				: {
						id: STANDARD_POSITIONS.othersReview,
						name: translate("common:filter.status.others-review"),
						display: true,
						isReviewer: true,
				  },
			{
				id: STANDARD_POSITIONS.myValidation,
				name: myCompany.name,
				display: true,
			},
		];
		if (isSegFeatureEnabled(SegFlags.PARTNERS)) {
			tempPartnerList = [...tempPartnerList, ...partners];
		}
		tempPartnerList = tempPartnerList.map((partner) => {
			if (localValue.some((lValue) => lValue.key === partner.id)) {
				return {
					...partner,
					helperText: "common:filter.status.validation-positions",
					display: false,
				};
			}
			return partner;
		});
		setFullPartnerSelection(tempPartnerList);
	}, [partners, projectId, expandableOthersReview, myCompany, localValue]);
	useEffect(() => {
		if (projectId && expandableOthersReview) {
			setReviewersRequest(
				() =>
					({ page, limit, search }) =>
						getProjectReviewers({ projectId }, { page, limit, search })
			);
		}
	}, [projectId, expandableOthersReview, getProjectReviewers]);
	useEffectOnce(
		() => {
			const init = async () => {
				let tempValue = [];
				if (value.myReview?.elements) {
					tempValue = [...tempValue, { key: "myReview", value: value.myReview.elements }];
				}
				if (value.othersReview?.elements) {
					tempValue = [...tempValue, { key: "othersReview", value: value.othersReview.elements }];
				}
				if (value.myValidation?.elements) {
					tempValue = [...tempValue, { key: "myValidation", value: value.myValidation.elements }];
				}
				if (
					Array.isArray(value.othersReviewByUser?.elements) &&
					value.othersReviewByUser?.elements.length > 0
				) {
					const userIds = value.othersReviewByUser.elements.map((user) => user.userId);
					try {
						const data = await getUserNameByIds({ projectId }, { ids: userIds });
						const userMap = Object.fromEntries(data.map((user) => [user.id, user.displayName]));
						value.othersReviewByUser.elements.forEach((user) => {
							tempValue = [
								...tempValue,
								{
									key: user.userId,
									value: user.status,
									companyId: user.companyId,
									userName: userMap[user.userId],
								},
							];
						});
					} catch (e) {
						console.error(e);
					}
				}
				if (Array.isArray(value.positions?.elements) && value.positions?.elements.length > 0) {
					value.positions.elements.forEach((company) => {
						tempValue = [...tempValue, { key: company.companyId, value: company.status }];
					});
				}
				if (tempValue.length === 0) {
					tempValue = [{ key: "myReview", value: [] }];
				}
				if (value?.separator) {
					setAndOr(value.separator);
				}
				setLocalValue(tempValue);
			};
			init();
		},
		[value],
		() => value
	);
	useEffect(() => {
		setReset(Object.keys(value).length === 0);
	}, [value]);
	useEffect(() => {
		if (reset && !preventReset.current) {
			setLocalValue([{ key: "myReview", value: [] }]);
		}
	}, [reset]);
	const clearEmptyPositions = (filterValue) => {
		const tempFilterValue = JSON.parse(JSON.stringify(filterValue));
		if (
			tempFilterValue.myReview?.elements?.length === undefined ||
			tempFilterValue.myReview?.elements?.length === 0
		) {
			delete tempFilterValue.myReview;
		}
		if (
			tempFilterValue.myValidation?.elements?.length === undefined ||
			tempFilterValue.myValidation?.elements?.length === 0
		) {
			delete tempFilterValue.myValidation;
		}
		if (
			tempFilterValue.othersReview?.elements?.length === undefined ||
			tempFilterValue.othersReview?.elements?.length === 0
		) {
			delete tempFilterValue.othersReview;
		}
		if (tempFilterValue?.othersReviewByUser?.elements?.length > 0) {
			tempFilterValue.othersReviewByUser.elements = tempFilterValue.othersReviewByUser.elements.filter(
				(pos) => pos.status?.length > 0
			);
		}
		if (
			tempFilterValue.othersReviewByUser?.elements?.length === undefined ||
			tempFilterValue.othersReviewByUser?.elements?.length === 0
		) {
			delete tempFilterValue.othersReviewByUser;
		}
		if (tempFilterValue?.positions?.elements?.length > 0) {
			tempFilterValue.positions.elements = tempFilterValue.positions.elements.filter(
				(pos) => pos.status?.length > 0
			);
		}
		if (
			tempFilterValue.positions?.elements?.length === undefined ||
			tempFilterValue.positions?.elements?.length === 0
		) {
			delete tempFilterValue.positions;
		}
		if (
			tempFilterValue.myReview === undefined &&
			tempFilterValue.myValidation === undefined &&
			tempFilterValue.othersReview === undefined &&
			tempFilterValue.positions === undefined
		) {
			delete tempFilterValue.separator;
		}
		return tempFilterValue;
	};
	const handleChangeAndOr = (entry) => {
		if (readOnly) {
			return;
		}
		setAndOr(entry);
		const tempValue = { ...value };
		tempValue.separator = entry;
		onChange(clearEmptyPositions(tempValue));
	};
	const handleSetAddNew = () => {
		setLocalValue([
			...localValue,
			{ key: fullPartnerSelection.filter((partner) => partner.display)[0].id, value: [] },
		]);
	};
	const handleChange = (newValue) => {
		newValue.separator = andOr;
		const isDefined = Object.values(newValue).some((v) => v !== undefined && (!Array.isArray(v) || v.length > 0));
		onChange(isDefined ? clearEmptyPositions(newValue) : undefined);
	};
	const handleChangeParams = (key, e) => {
		const filter = value[key]
			? { ...value[key], elements: value[key].elements || [] }
			: { elements: [], is: true, matchAll: false };
		const newValue = e.target.value.toUpperCase();
		if (filter.elements.some((v) => v === newValue)) {
			filter.elements = filter.elements.filter((v) => v !== newValue);
		} else {
			filter.elements = [...filter.elements, newValue];
		}
		handleChange({ ...value, [key]: filter });
		const tempLocalValue = [...localValue];
		setLocalValue(tempLocalValue.map((lValue) => (lValue.key === key ? { key, value: filter.elements } : lValue)));
	};
	const handleClickOtherUsersReview = (user, e) => {
		const { key: userId, userName } = user;
		const { value: newValue } = e.target;
		const filter = value.othersReviewByUser
			? { ...value.othersReviewByUser }
			: { elements: [], is: true, matchAll: false };
		let othersReviewByUserFilter;
		if (filter.elements.some((c) => c.userId === userId)) {
			othersReviewByUserFilter = { ...filter.elements.find((c) => c.userId === userId) };
			filter.elements = filter.elements.filter((c) => c.userId !== userId);
		} else {
			othersReviewByUserFilter = { userId, status: [] };
		}
		if (!othersReviewByUserFilter.status) {
			othersReviewByUserFilter.status = [];
		}
		if (othersReviewByUserFilter.status.some((v) => v === newValue)) {
			othersReviewByUserFilter.status = othersReviewByUserFilter.status.filter((v) => v !== newValue);
		} else {
			othersReviewByUserFilter.status = [...othersReviewByUserFilter.status, newValue];
		}
		if (othersReviewByUserFilter.status.length !== 0) {
			filter.elements.push(othersReviewByUserFilter);
		}
		handleChange({
			...value,
			othersReviewByUser: {
				...filter,
				elements: filter.elements.map((el) => ({ ...el, companyId: myCompany.id })),
			},
		});
		const tempLocalValue = [...localValue];
		setLocalValue(
			tempLocalValue.map((lValue) =>
				lValue.key === userId ? { key: userId, value: othersReviewByUserFilter.status, userName } : lValue
			)
		);
	};
	const handleClickCompanies = (companyId, e) => {
		const { value: newValue } = e.target;
		const filter = value.positions ? { ...value.positions } : { elements: [], is: true, matchAll: false };
		let companyFilter;
		if (filter.elements.some((c) => c.companyId === companyId)) {
			companyFilter = { ...filter.elements.find((c) => c.companyId === companyId) };
			filter.elements = filter.elements.filter((c) => c.companyId !== companyId);
		} else {
			companyFilter = { companyId, status: [] };
		}
		if (!companyFilter.status) {
			companyFilter.status = [];
		}
		if (companyFilter.status.some((v) => v === newValue)) {
			companyFilter.status = companyFilter.status.filter((v) => v !== newValue);
		} else {
			companyFilter.status = [...companyFilter.status, newValue];
		}
		if (companyFilter.status.length !== 0) {
			filter.elements.push(companyFilter);
		}
		handleChange({ ...value, positions: filter });
		const tempLocalValue = [...localValue];
		setLocalValue(
			tempLocalValue.map((lValue) =>
				lValue.key === companyId ? { key: companyId, value: companyFilter.status } : lValue
			)
		);
	};
	const handleSelectStatus = (key, e) => {
		if (readOnly) {
			return;
		}
		preventReset.current = true;
		if (
			[STANDARD_POSITIONS.myReview, STANDARD_POSITIONS.othersReview, STANDARD_POSITIONS.myValidation].includes(
				key.key
			)
		) {
			handleChangeParams(key.key, e);
		} else if (key.userName) {
			handleClickOtherUsersReview(key, e);
		} else {
			handleClickCompanies(key.key, e);
		}
		debouncedFunction(() => {
			preventReset.current = false;
		});
	};
	const handleSetActiveCompany = (oldKey, newKey) => {
		const tempLocalValue = [...localValue];
		const SIMPLE_STANDARD_KEYS = [
			STANDARD_POSITIONS.myReview,
			STANDARD_POSITIONS.othersReview,
			STANDARD_POSITIONS.myValidation,
		];
		const isOldSimpleStandard = SIMPLE_STANDARD_KEYS.includes(oldKey);
		const isNewSimpleStandard = SIMPLE_STANDARD_KEYS.includes(newKey?.id);
		const isNewOBU = newKey?.id === STANDARD_POSITIONS.othersReviewByUser && !!newKey?.user;
		const isOldOBU =
			!isOldSimpleStandard && !!value.othersReviewByUser?.elements?.some((el) => el.userId === oldKey);
		setLocalValue(
			tempLocalValue.map((lValue) =>
				lValue.key === oldKey
					? {
							value: lValue.value,
							key: isNewOBU ? newKey.user.id : newKey?.id,
							userName: isNewOBU ? newKey.user.displayName : undefined,
					  }
					: lValue
			)
		);
		const tempValue = { ...value };
		// myReview, othersReview, myValidation -> myReview, othersReview, myValidation
		if (isOldSimpleStandard && isNewSimpleStandard) {
			delete Object.assign(tempValue, { [newKey?.id]: tempValue[oldKey] })[oldKey];
		}
		// myReview, othersReview, myValidation -> partners review
		if (isOldSimpleStandard && !isNewSimpleStandard && !isNewOBU) {
			if (Array.isArray(tempValue?.positions?.elements) && tempValue.positions.elements.length > 0) {
				tempValue.positions.elements = [
					...tempValue.positions.elements,
					{ companyId: newKey?.id, status: tempValue[oldKey].elements },
				];
			} else if (isNonEmptyArray(tempValue[oldKey]?.elements)) {
				tempValue.positions = {
					is: true,
					matchAll: false,
					elements: [{ companyId: newKey?.id, status: tempValue[oldKey]?.elements }],
				};
			}
			delete tempValue[oldKey];
		}
		// myReview, othersReview, myValidation -> othersReviewByUser
		if (isOldSimpleStandard && isNewOBU) {
			const currentStatus = tempValue[oldKey]?.elements || [];
			const existingOBU = tempValue.othersReviewByUser || { elements: [], is: true, matchAll: false };
			tempValue.othersReviewByUser = {
				...existingOBU,
				elements: [
					...existingOBU.elements.filter((el) => el.userId !== newKey.user.id),
					{ userId: newKey.user.id, companyId: myCompany?.id, status: currentStatus },
				],
			};
			delete tempValue[oldKey];
		}
		// partners review -> myReview, othersReview, myValidation
		if (!isOldSimpleStandard && !isOldOBU && isNewSimpleStandard) {
			tempValue[newKey?.id] = {
				elements: tempValue.positions?.elements.find((pos) => pos.companyId === oldKey).status,
				is: true,
				matchAll: false,
			};
			if (tempValue.positions?.elements.length === 1) {
				delete tempValue.positions;
			} else {
				tempValue.positions ??= { elements: [] };
				tempValue.positions.elements = tempValue.positions?.elements.filter((pos) => pos.companyId !== oldKey);
			}
		}
		// partners review -> partners review
		if (!isOldSimpleStandard && !isOldOBU && !isNewSimpleStandard && !isNewOBU) {
			tempValue.positions.elements = tempValue.positions.elements.map((lValue) =>
				lValue.companyId === oldKey ? { status: lValue.status, companyId: newKey?.id } : lValue
			);
		}
		// partners review -> othersReviewByUser
		if (!isOldSimpleStandard && !isOldOBU && isNewOBU) {
			const currentStatus = tempValue.positions?.elements.find((c) => c.companyId === oldKey)?.status || [];
			const existingOBU = tempValue.othersReviewByUser || { elements: [], is: true, matchAll: false };
			tempValue.othersReviewByUser = {
				...existingOBU,
				elements: [
					...existingOBU.elements.filter((el) => el.userId !== newKey.user.id),
					{ userId: newKey.user.id, companyId: myCompany?.id, status: currentStatus },
				],
			};
			if (tempValue.positions?.elements.length === 1) {
				delete tempValue.positions;
			} else {
				tempValue.positions ??= { elements: [] };
				tempValue.positions.elements = tempValue.positions?.elements.filter((c) => c.companyId !== oldKey);
			}
		}
		// othersReviewByUser -> myReview, othersReview, myValidation
		if (isOldOBU && isNewSimpleStandard) {
			const userEntry = tempValue.othersReviewByUser?.elements.find((el) => el.userId === oldKey);
			tempValue[newKey?.id] = { elements: userEntry?.status || [], is: true, matchAll: false };
			const remainingElements = tempValue.othersReviewByUser.elements.filter((el) => el.userId !== oldKey);
			if (remainingElements.length === 0) {
				delete tempValue.othersReviewByUser;
			} else {
				tempValue.othersReviewByUser = { ...tempValue.othersReviewByUser, elements: remainingElements };
			}
		}
		// othersReviewByUser -> partners review
		if (isOldOBU && !isNewSimpleStandard && !isNewOBU) {
			const userEntry = tempValue.othersReviewByUser?.elements.find((el) => el.userId === oldKey);
			const currentStatus = userEntry?.status || [];
			if (Array.isArray(tempValue?.positions?.elements) && tempValue.positions.elements.length > 0) {
				tempValue.positions.elements = [
					...tempValue.positions.elements,
					{ companyId: newKey?.id, status: currentStatus },
				];
			} else {
				tempValue.positions = {
					is: true,
					matchAll: false,
					elements: [{ companyId: newKey?.id, status: currentStatus }],
				};
			}
			const remainingElements = tempValue.othersReviewByUser.elements.filter((el) => el.userId !== oldKey);
			if (remainingElements.length === 0) {
				delete tempValue.othersReviewByUser;
			} else {
				tempValue.othersReviewByUser = { ...tempValue.othersReviewByUser, elements: remainingElements };
			}
		}
		// othersReviewByUser -> othersReviewByUser (change user)
		if (isOldOBU && isNewOBU) {
			const userEntry = tempValue.othersReviewByUser?.elements.find((el) => el.userId === oldKey);
			const currentStatus = userEntry?.status || [];
			const elements = [
				...tempValue.othersReviewByUser.elements.filter(
					(el) => el.userId !== oldKey && el.userId !== newKey.user.id
				),
				{ userId: newKey.user.id, companyId: myCompany?.id, status: currentStatus },
			];
			tempValue.othersReviewByUser = { ...tempValue.othersReviewByUser, elements };
		}
		if (
			tempValue?.myReview !== undefined ||
			tempValue?.othersReview !== undefined ||
			tempValue?.myValidation !== undefined ||
			tempValue?.othersReviewByUser !== undefined ||
			tempValue?.positions?.elements?.some((el) => el.status !== undefined)
		) {
			tempValue.separator = andOr;
			onChange(clearEmptyPositions(tempValue));
		}
	};
	const handleRemove = (keyToRemove, isUser) => {
		const tempLocalValue = [...localValue];
		setLocalValue(tempLocalValue.filter((lValue) => lValue.key !== keyToRemove));
		const tempParentValue = { ...value };
		if (
			[STANDARD_POSITIONS.myReview, STANDARD_POSITIONS.othersReview, STANDARD_POSITIONS.myValidation].includes(
				keyToRemove
			)
		) {
			delete tempParentValue[keyToRemove];
		} else if (isUser) {
			tempParentValue.othersReviewByUser.elements = tempParentValue.othersReviewByUser.elements.filter(
				(user) => user.userId !== keyToRemove
			);
			if (
				!Array.isArray(tempParentValue.othersReviewByUser.elements) ||
				tempParentValue.othersReviewByUser.elements.length === 0
			) {
				delete tempParentValue.othersReviewByUser;
			}
		} else {
			tempParentValue.positions ??= { elements: [] };
			tempParentValue.positions.elements = tempParentValue.positions?.elements.filter(
				(company) => company.companyId !== keyToRemove
			);
			if (!Array.isArray(tempParentValue.positions.elements) || tempParentValue.positions.elements.length === 0) {
				delete tempParentValue.positions;
			}
		}
		tempParentValue.separator = andOr;
		onChange(tempParentValue);
	};

	return (
		<div>
			{isNonEmptyArray(localValue) && (
				<>
					<StatusIcons size="md" />
					{localValue.map((lValue, index) => (
						<StatusButtons
							key={lValue.key}
							showUnreviewed
							activeCompany={lValue.key}
							andOr={andOr}
							order={index}
							partnersSelection={fullPartnerSelection}
							positionValue={lValue.value}
							readOnly={readOnly}
							reviewersRequest={reviewersRequest}
							selectedUsers={localValue.filter((lv) => !!lv.userName).map((lv) => lv.key)}
							userName={lValue.userName}
							onChangeAndOr={handleChangeAndOr}
							onRemove={handleRemove}
							onSelectStatus={handleSelectStatus}
							onSetActiveCompany={handleSetActiveCompany}
						/>
					))}
				</>
			)}
			{Array.isArray(fullPartnerSelection) &&
				fullPartnerSelection.filter((partner) => partner.display).length > 0 && (
					<div className={styles.addFilter__container}>
						<CustomButton
							disabled={readOnly}
							size="sm"
							startIcon={icon.faPlus}
							variant="outlined"
							onClick={handleSetAddNew}
						>
							{translate("common:filters.select-filter.title")}
						</CustomButton>
					</div>
				)}
		</div>
	);
}
